/**
 * JsonRenderer - Collapsible JSON tree view
 *
 * Detects JSON content and renders as an interactive tree.
 * Returns null if content is not valid JSON, allowing fallback to MathMarkdown.
 */

import { memo, useState, useCallback } from 'react';
import '../../styles/json-renderer.css';

interface JsonRendererProps {
  children: string;
  className?: string;
}

/** Try parsing text as JSON. Returns parsed value or null. */
export function tryParseJson(text: string): unknown | null {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

interface JsonNodeProps {
  keyName?: string;
  value: unknown;
  depth: number;
}

function JsonNode({ keyName, value, depth }: JsonNodeProps) {
  const [collapsed, setCollapsed] = useState(depth >= 2);

  const toggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed((c) => !c);
  }, []);

  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);

  if (!isObject) {
    // Leaf value
    let cls = 'json-tree__value';
    if (typeof value === 'string') cls += ' json-tree__value--string';
    else if (typeof value === 'number') cls += ' json-tree__value--number';
    else if (typeof value === 'boolean') cls += ' json-tree__value--boolean';
    else if (value === null) cls += ' json-tree__value--null';

    const display = typeof value === 'string' ? `"${value}"` : String(value);

    return (
      <div className="json-tree__leaf">
        {keyName !== undefined && (
          <span className="json-tree__key">{keyName}: </span>
        )}
        <span className={cls}>{display}</span>
      </div>
    );
  }

  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  const bracket = isArray ? ['[', ']'] : ['{', '}'];
  const count = entries.length;

  return (
    <div className="json-tree__node">
      <div className="json-tree__header" onClick={toggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(c => !c); } }}>
        <span className={`json-tree__toggle ${collapsed ? '' : 'json-tree__toggle--open'}`}>
          &#x25B6;
        </span>
        {keyName !== undefined && (
          <span className="json-tree__key">{keyName}: </span>
        )}
        <span className="json-tree__bracket">{bracket[0]}</span>
        {collapsed && (
          <span className="json-tree__summary">
            {count} {isArray ? 'items' : 'keys'} {bracket[1]}
          </span>
        )}
      </div>
      {!collapsed && (
        <div className="json-tree__children">
          {entries.map(([k, v]) => (
            <JsonNode key={k} keyName={isArray ? undefined : k} value={v} depth={depth + 1} />
          ))}
          <div className="json-tree__bracket">{bracket[1]}</div>
        </div>
      )}
    </div>
  );
}

function JsonRendererInner({ children, className = '' }: JsonRendererProps) {
  const parsed = tryParseJson(children);
  if (parsed === null) return null;

  return (
    <div className={`json-tree ${className}`}>
      <JsonNode value={parsed} depth={0} />
    </div>
  );
}

export const JsonRenderer = memo(JsonRendererInner);
export default JsonRenderer;
